/**
 * Transforms a curriculum-service `/v1/page-content` response into the shape
 * the player already expects from the content service, so the existing
 * `mapContentServiceToContentfulResponse` pipeline works unchanged.
 *
 * Parity contract:
 *   content service → { presentation: { displayTitle, id, ...fields, sections[] } }
 *
 * Curriculum service shape (flat ContentPage):
 *   { page: { id, type, title, display_title, ...contentfulFields } }
 *
 * As of curriculum-service, the page no longer splits Contentful entries into
 * `sections[]` + `metadata{}`. `normalizeContentfulPage` now spreads the entry
 * fields directly onto the page object alongside `id`, `type`, and `title`
 * (mirroring content-service's `sanitizeGraphQL`). This transform therefore
 * only needs to camelCase the snake_case keys the service serializes.
 *
 * Mirrors secondstep-learn's
 * `src/layers/content/v3/transforms/transformCurriculumResponse.js`, scoped to
 * the player's single content type (presentation).
 */

function snakeToCamel(str) {
  return str.replace(/_([a-z0-9])/g, (_, ch) => ch.toUpperCase())
}

/**
 * Recursively converts all snake_case object keys to camelCase. Idempotent on
 * already-camelCase keys, so it is safe to apply regardless of how the service
 * serializes a given segment. Leaves arrays, primitives, and null unchanged.
 */
function camelizeKeys(obj) {
  if (obj == null || typeof obj !== 'object') return obj
  if (Array.isArray(obj)) return obj.map(camelizeKeys)

  const result = {}
  for (const [key, value] of Object.entries(obj)) {
    result[snakeToCamel(key)] = camelizeKeys(value)
  }
  return result
}

/**
 * Maps a curriculum-service `/v1/page-content` response to the content-service
 * `{ presentation }` shape consumed by `mapContentServiceToContentfulResponse`.
 *
 * Accepts any of:
 *   - a response already in content-service shape ({ presentation })
 *   - a wrapped page ({ page: <ContentPage> })
 *   - a bare ContentPage / raw presentation object
 *
 * @param {Object} curriculumResponse
 * @returns {{ presentation: Object }}
 */
export function mapCurriculumServiceToContentServiceResponse(
  curriculumResponse,
) {
  if (curriculumResponse == null) {
    return { presentation: {} }
  }

  // Already in content-service shape — pass through.
  if (curriculumResponse.presentation != null) {
    return { presentation: curriculumResponse.presentation }
  }

  const page = curriculumResponse.page || curriculumResponse
  const presentation = camelizeKeys(page)

  return { presentation }
}
